import type { ContractorTranslation } from './en';

export const zhHans: ContractorTranslation = {
  common: {
    cancel: '取消',
    save: '保存',
    retry: '重试',
    download: '下载',
    continue: '继续',
    yes: '是',
    no: '否',
    genericError: '出了点问题，请重试。',
    networkError: '无法连接到 DubForge，请检查网络连接。',
  },
  app: {
    tagline: '配音任务、文件和审核，集中管理。',
    startupFailed: 'DubForge 无法启动',
    notFoundTitle: '找不到页面',
    notFoundBody: '该页面不存在，或者你没有访问权限。',
    goHome: '返回首页',
    crashTitle: '出了点问题',
    crashBody: '请尝试刷新页面。',
    reload: '刷新',
  },
  language: {
    label: '语言',
    change: '切换语言',
  },
  welcome: {
    title: '欢迎使用 DubForge',
    body: '请选择你的语言。DubForge 界面和 Discord 通知都会使用该语言。',
    changeLater: '之后可以随时通过右上角的语言菜单更改。',
  },
  nav: {
    myAssignments: '我的任务',
    openMenu: '打开菜单',
    account: '账户',
    notificationEmail: '通知邮箱',
    signOut: '退出登录',
    roleAdmin: '管理员',
    roleContractor: '配音员',
    lightMode: '浅色模式',
    darkMode: '深色模式',
  },
  login: {
    signIn: '使用 Discord 登录',
    onlyInvited: '只有频道所有者添加的 Discord 账户才能登录。',
    signingIn: '正在登录…',
    backToSignIn: '返回登录页',
    failed: '登录失败，请重试。',
  },
  authErrors: {
    not_invited: '此 Discord 账户尚未添加到 DubForge。请联系频道所有者添加你。',
    access_off: '你的 DubForge 访问权限已被关闭。',
    discord_only: '只能使用 Discord 登录。',
  },
  errors: {
    not_signed_in: '登录已过期，请重新登录。',
    access_off: '你的 DubForge 访问权限已被关闭。',
    admins_only: '你没有执行此操作的权限。',
    not_found: '该内容不存在或已不可用。',
    invalid_request: '请求中有无效内容，请检查后重试。',
    conflict: '该内容已存在。',
    already_approved: '此任务已通过审核，无需再上传。',
    not_audio: '只能上传 WAV 或 MP3 文件。请只上传你的人声音轨。',
    upload_incomplete: '上传未完成，请重试。',
    discord_unreachable: '无法连接到 Discord，请稍后重试。',
    server_error: '服务器出了点问题，请重试。',
  },
  notifications: {
    title: '通知',
    openWithCount: '通知（{{count}} 条未读）',
    markAllRead: '全部标为已读',
    empty: '暂无通知。',
  },
  emailSettings: {
    body: '当你收到新任务或作品审核完成时，我们会发邮件通知你。',
    label: '邮箱地址',
    fromDiscord: '取自你的 Discord 账户。如果想用其他地址，可以修改。',
    saved: '邮箱地址已保存',
    invalid: '请输入有效的邮箱地址。',
    missing: '还没有邮箱地址。添加后即可接收通知。',
  },
  status: {
    Assigned: '已分配',
    InProgress: '进行中',
    Submitted: '等待审核',
    ChangesRequested: '需要修改',
    Approved: '已通过',
  },
  assetKind: {
    package: '配音素材包（视频 + 人声 + SFX）',
    video: '参考视频（720p）',
    sfx: 'SFX／音乐音轨',
    voice: '原始人声音轨',
    script: '脚本',
    other: '其他',
  },
  translation: {
    machine: '机器翻译',
    showOriginal: '查看原文',
    showTranslation: '查看译文',
  },
  uploader: {
    progress: '{{percent}}%，共 {{size}}',
    queued: '等待上传：{{count}} 个',
    keepOpen: '上传完成前请不要关闭此标签页。',
    cancelled: '已取消上传',
    done: '已上传 {{name}}',
    failed: '上传失败：{{error}}',
  },
  my: {
    greeting: '你好，{{name}}',
    subtitle: '你的配音任务。',
    emptyTitle: '暂无任务',
    emptyBody: '当你被分配视频时，会收到 Discord 消息，任务也会显示在这里。',
    todo: '待处理',
    waiting: '等待审核',
    done: '已完成',
    updated: '{{when}}更新',
  },
  assignment: {
    back: '我的任务',
    dubIn: '配音语言：{{language}}',
    paid: '已付款',
    next: {
      Assigned: '下载素材文件，录制你的声音，然后在这里上传你的人声音轨。',
      InProgress: '人声音轨准备好后，请在下方上传。',
      Submitted: '你的配音已提交审核。审核完成后，你会收到 Discord 消息。',
      ChangesRequested: '需要修改。请查看下方的反馈，然后上传新版本。',
      Approved: '已通过审核。谢谢！',
    },
    notes: '备注',
    sourceFiles: '素材文件',
    noFiles: '还没有上传任何文件。',
    fileInfo: '{{kind}} · {{size}}',
    packageHint: '一个包含视频和两条音轨的 .mp4 文件：音轨 1 是原始台词人声，音轨 2 是 SFX。',
    uploadTitle: '上传你的人声音轨',
    voiceOnlyTitle: '只要你的声音',
    voiceOnlyBody: '请只上传你录制的人声：不要包含音乐、音效或任何原始音频。请确保时长与原始音频一致。',
    voiceOnlyConfirm: '此文件只包含台词人声。',
    dropLabel: '在此上传人声音轨',
    dropHint: '仅限 WAV 或 MP3。',
    noteForReviewer: '给审核人员的备注（可选）',
    lengthTitle: '时长不一致',
    lengthBody:
      '你的文件时长为 {{dub}}，但 SFX 音轨时长为 {{sfx}}。如果你的音轨起点不同或被截断，就无法与视频对齐。仍要上传吗？',
    uploadAnyway: '仍然上传',
    chooseAnother: '选择其他文件',
    versions: '已上传的版本',
    nothingUploaded: '还没有上传任何内容。',
    version: '第 {{version}} 版',
    reviewApproved: '已通过',
    reviewChanges: '需要修改',
    reviewPending: '等待审核',
    yourNote: '你的备注：',
    feedback: '反馈',
    listen: '试听',
    uploadedOn: '{{date}} · {{size}}',
    length: '时长：{{duration}}',
  },
};
